import { useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, RequireAuth, useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import { setAccessTokenGetter } from "@/lib/api";
import { ToastProvider } from "@/components/ui/toast";
import { Layout } from "@/components/Layout";
import Dashboard from "@/routes/Dashboard";
import AppDetail from "@/routes/AppDetail";
import Jobs from "@/routes/Jobs";
import JobDetail from "@/routes/JobDetail";
import AuthCallbackRoute from "@/routes/AuthCallback";

const router = createBrowserRouter([
  {
    path: "/auth/callback",
    element: <AuthCallbackRoute />,
  },
  {
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "apps/:appId", element: <AppDetail /> },
      { path: "jobs", element: <Jobs /> },
      { path: "jobs/:jobId", element: <JobDetail /> },
    ],
  },
]);

function TokenBridge() {
  const auth = useAuth();

  useEffect(() => {
    // Send id_token (not access_token) — Cognito access tokens omit email,
    // which the backend auth middleware requires for domain checking.
    setAccessTokenGetter(async () => auth.user?.id_token);
  }, [auth.user]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <TokenBridge />
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
